import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import workflowService from '../workflow-aprovacao/workflowService';
import { escaparHtml, justificativaPadraoReset, resumoDoReset } from './justificativaReset';

interface Props {
  clienteId: number;
  nomeCliente: string;
  ativo: boolean;
}

type OnboardingStatus = {
  etapa1Concluida: boolean; etapa2Concluida: boolean;
  etapa3Concluida: boolean; etapa4Concluida: boolean; etapa5Concluida: boolean;
  goLiveAtivo: boolean; goLiveEm: string | null;
  etapasConcluidas: number; progressoPercent: number;
  bloqueios: { etapa: number; titulo: string; motivo: string }[];
};

// Mesmos nomes das etapas do assistente de onboarding (Workflow SOX → Onboarding).
const ETAPAS_LABEL = ['Hierarquia e De/Para', 'Cargos, Usuários e Alçadas', 'Matriz de Aprovadores', 'SLA e Travas de Governança', 'Validação e Go-Live'];

const ClientesWorkflowTab = ({ clienteId, nomeCliente, ativo }: Props) => {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [resetando, setResetando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!ativo || clienteId <= 0) return;
    setCarregando(true);
    setErro(null);
    workflowService.onboardingStatus(clienteId)
      .then(setStatus)
      .catch(() => setErro('Não foi possível carregar o status do Workflow SOX.'))
      .finally(() => setCarregando(false));
  }, [ativo, clienteId]);

  async function resetar() {
    const { value: justificativa, isConfirmed } = await Swal.fire({
      title: 'Resetar configuração WorkFlow',
      html: `
        <p style="font-size:14px;color:#7e8299;margin-bottom:8px">
          Esta ação irá reiniciar o processo de onboarding para <strong>${escaparHtml(nomeCliente)}</strong>.
        </p>
        <p style="font-size:13px;color:#f64e60;margin-bottom:16px">
          ⚠️ As etapas do onboarding serão zeradas; nas empresas deste cliente, as conciliações em aberto serão canceladas e os acessos, a matriz de responsabilidades e o De/Para serão removidos. O histórico SOX de auditoria é preservado.
        </p>
        <label style="display:block;font-size:13px;font-weight:600;text-align:left;margin-bottom:4px">
          Justificativa SOX (obrigatória):
        </label>
        <textarea id="wf-reset-justificativa" class="swal2-textarea" placeholder="Descreva o motivo do reset da configuração..." rows="4" style="width:100%;box-sizing:border-box">${escaparHtml(justificativaPadraoReset(nomeCliente))}</textarea>
      `,
      // Já vem preenchida; ao abrir, o cursor vai para o fim do texto para a pessoa editar se quiser.
      didOpen: () => {
        const campo = document.getElementById('wf-reset-justificativa') as HTMLTextAreaElement | null;
        if (!campo) return;
        campo.focus();
        campo.setSelectionRange(campo.value.length, campo.value.length);
      },
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Confirmar Reset',
      reverseButtons: true,
      customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
      buttonsStyling: false,
      preConfirm: () => {
        const val = (document.getElementById('wf-reset-justificativa') as HTMLTextAreaElement)?.value?.trim();
        if (!val) { Swal.showValidationMessage('A justificativa SOX é obrigatória.'); return false; }
        return val;
      },
    });

    if (!isConfirmed || !justificativa) return;

    setResetando(true);
    try {
      const resultado = await workflowService.resetOnboarding(clienteId, justificativa as string);
      const novoStatus = await workflowService.onboardingStatus(clienteId);
      setStatus(novoStatus);
      const resumo = resumoDoReset(resultado);
      Swal.fire({
        icon: 'success',
        title: 'Configuração WorkFlow resetada',
        html: `<p style="font-size:14px">O cliente poderá iniciar um novo onboarding.</p>${resumo.length ? `<ul style="text-align:left;font-size:13px">${resumo.map(l => `<li>${escaparHtml(l)}</li>`).join('')}</ul>` : ''}`,
        confirmButtonText: 'OK',
      });
    } catch {
      Swal.fire({ text: 'Erro ao resetar a configuração WorkFlow.', icon: 'error', timer: 3000, showConfirmButton: false });
    } finally {
      setResetando(false);
    }
  }

  if (!ativo) return null;

  return (
    <div style={{ padding: '10px' }}>
      <div className='card card-custom card-stretch'>
        <div className='card-header border-0 pt-4'>
          <h4 className='card-title fw-bolder text-dark'>Workflow SOX — Onboarding</h4>
        </div>

        <div className='card-body pt-2'>
          {erro && <div className='alert alert-danger py-2 px-4 mb-4'>{erro}</div>}

          {carregando && (
            <div className='text-center py-8'><span className='spinner-border text-primary' /></div>
          )}

          {!carregando && status && (
            <>
              <div className='row g-3 mb-5'>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Status</div>
                    <div>
                      {status.goLiveAtivo
                        ? <span className='badge badge-light-success'>Go-Live Ativo</span>
                        : status.etapasConcluidas === 0
                          ? <span className='badge badge-light-secondary'>Não iniciado</span>
                          : <span className='badge badge-light-warning'>Em configuração</span>}
                    </div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Etapas concluídas</div>
                    <div className='fw-bolder fs-4'>{status.etapasConcluidas} / 5</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-light p-4 text-center h-100'>
                    <div className='text-muted fs-8 mb-2'>Progresso</div>
                    <div className='fw-bolder fs-4'>{status.progressoPercent}%</div>
                  </div>
                </div>
              </div>

              <ul className='list-group mb-5'>
                {ETAPAS_LABEL.map((label, i) => {
                  const concluida = [status.etapa1Concluida, status.etapa2Concluida, status.etapa3Concluida, status.etapa4Concluida, status.etapa5Concluida][i];
                  return (
                    <li key={i} className='list-group-item d-flex justify-content-between align-items-center'>
                      <span>{i + 1}. {label}</span>
                      {concluida
                        ? <span className='badge badge-light-success'>Concluída</span>
                        : <span className='badge badge-light-secondary'>Pendente</span>}
                    </li>
                  );
                })}
              </ul>

              {status.bloqueios.length > 0 && (
                <div className='alert alert-warning py-2 px-4 mb-5'>
                  <strong>Bloqueios ativos:</strong>
                  <ul className='mb-0 mt-1'>
                    {status.bloqueios.map((b, i) => <li key={i}><strong>Etapa {b.etapa} — {b.titulo}:</strong> {b.motivo}</li>)}
                  </ul>
                </div>
              )}

              <div className='separator my-4' />
              <h6 className='fw-bold mb-2'>Resetar configuração</h6>
              <p className='text-muted fs-8 mb-3'>
                Zera as 5 etapas de onboarding do Workflow SOX e, nas empresas deste cliente, cancela as conciliações em aberto e remove acessos usuário × empresa, matriz de responsabilidades e vínculos De/Para. O histórico SOX, os grupos econômicos, cargos, alçadas e usuários são preservados.
              </p>
              <button type='button' className='btn btn-danger' disabled={resetando} onClick={resetar}>
                {resetando ? <span className='spinner-border spinner-border-sm me-2' /> : <i className='bi bi-arrow-counterclockwise me-2' />}
                Resetar configuração WorkFlow
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientesWorkflowTab;
